import React, { useState, useMemo } from 'react';
import {
  Briefcase,
  Search,
  Filter,
  Plus,
  ArrowUpDown,
  Kanban,
  List,
  Layers,
  ChevronRight,
  User,
  Zap,
  CheckCircle2
} from 'lucide-react';
import { PracticeCase, CaseStage, PriorityLevel } from '../../types/practiceManagement';
import { Language } from '../../types/kundali';
import { CaseCard } from './CaseCard';

interface CasePipelineProps {
  cases: PracticeCase[];
  lang: Language;
  onOpenCaseWorkspace: (caseItem: PracticeCase) => void;
  onOpenProAnalysis?: (caseItem: PracticeCase) => void;
  onOpenAcharyaConsultation?: (caseItem: PracticeCase) => void;
  onPrepareReport?: (caseItem: PracticeCase) => void;
  onNewCase: () => void;
  onMoveStage: (caseId: string, newStage: CaseStage) => void;
}

const PIPELINE_STAGES: Array<{ id: CaseStage; labelEn: string; labelHi: string; descEn: string }> = [
  { id: 'NEW', labelEn: 'New', labelHi: 'नया', descEn: 'Intake received' },
  { id: 'ANALYSIS', labelEn: 'Analysis', labelHi: 'विश्लेषण', descEn: 'Evidence evaluation' },
  { id: 'REVIEW', labelEn: 'Review', labelHi: 'समीक्षा', descEn: 'Classical verification' },
  { id: 'CONSULTATION', labelEn: 'Consultation', labelHi: 'परामर्श', descEn: 'Active session' },
  { id: 'REPORT_PREP', labelEn: 'Report Prep', labelHi: 'रिपोर्ट', descEn: 'Dossier drafting' },
  { id: 'DELIVERED', labelEn: 'Delivered', labelHi: 'वितरित', descEn: 'Report handed over' },
  { id: 'FOLLOW_UP', labelEn: 'Follow-Up', labelHi: 'फॉलो-अप', descEn: 'Milestone tracking' }
];

export const CasePipeline: React.FC<CasePipelineProps> = ({
  cases,
  lang,
  onOpenCaseWorkspace,
  onOpenProAnalysis,
  onOpenAcharyaConsultation,
  onPrepareReport,
  onNewCase,
  onMoveStage
}) => {
  const isHi = lang === 'hi';
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedStageFilter, setSelectedStageFilter] = useState<string>('ALL');
  const [selectedPriorityFilter, setSelectedPriorityFilter] = useState<string>('ALL');
  const [viewMode, setViewMode] = useState<'board' | 'list'>('board');

  // Filtered cases
  const filteredCases = useMemo(() => {
    return cases.filter((c) => {
      const matchSearch =
        c.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.caseNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.themeTitleEn.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.targetQuestion.toLowerCase().includes(searchTerm.toLowerCase());

      const matchStage = selectedStageFilter === 'ALL' || c.stage === selectedStageFilter;
      const matchPriority = selectedPriorityFilter === 'ALL' || c.priority === selectedPriorityFilter;

      return matchSearch && matchStage && matchPriority;
    });
  }, [cases, searchTerm, selectedStageFilter, selectedPriorityFilter]);

  return (
    <div className="space-y-4">
      {/* Pipeline Toolbar */}
      <div className="bg-white dark:bg-slate-850 p-4 rounded-xl border border-astro-border/90 dark:border-astro-strong-border/80 shadow-xs flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-[240px]">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-astro-muted absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={isHi ? 'क्लाइंट, केस ID या विषय खोजें...' : 'Search client, case ID or question...'}
              className="w-full pl-9 pr-3 py-2 text-xs bg-astro-secondary-surface dark:bg-astro-primary rounded-lg border border-astro-border dark:border-astro-strong-border text-astro-text dark:text-astro-muted focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
          </div>

          <select
            value={selectedStageFilter}
            onChange={(e) => setSelectedStageFilter(e.target.value)}
            className="px-3 py-2 text-xs bg-astro-secondary-surface dark:bg-astro-primary rounded-lg border border-astro-border dark:border-astro-strong-border text-astro-text dark:text-astro-muted focus:outline-none focus:ring-2 focus:ring-amber-500"
          >
            <option value="ALL">{isHi ? 'सभी चरण (Stages)' : 'All Stages'}</option>
            {PIPELINE_STAGES.map((s) => (
              <option key={s.id} value={s.id}>
                {isHi ? s.labelHi : s.labelEn}
              </option>
            ))}
          </select>

          <select
            value={selectedPriorityFilter}
            onChange={(e) => setSelectedPriorityFilter(e.target.value)}
            className="px-3 py-2 text-xs bg-astro-secondary-surface dark:bg-astro-primary rounded-lg border border-astro-border dark:border-astro-strong-border text-astro-text dark:text-astro-muted focus:outline-none focus:ring-2 focus:ring-amber-500"
          >
            <option value="ALL">{isHi ? 'सभी प्राथमिकता' : 'All Priorities'}</option>
            <option value="HIGH">High Priority</option>
            <option value="MEDIUM">Standard</option>
            <option value="LOW">Routine</option>
          </select>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center bg-astro-secondary-surface dark:bg-astro-secondary p-1 rounded-lg border border-astro-border dark:border-astro-strong-border">
            <button
              onClick={() => setViewMode('board')}
              className={`p-1.5 rounded-md text-xs font-bold transition-all cursor-pointer ${
                viewMode === 'board'
                  ? 'bg-white dark:bg-astro-secondary text-astro-secondary dark:text-amber-200 shadow-xs'
                  : 'text-astro-muted hover:text-astro-text dark:hover:text-astro-muted'
              }`}
              title="Board View"
            >
              <Kanban className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-1.5 rounded-md text-xs font-bold transition-all cursor-pointer ${
                viewMode === 'list'
                  ? 'bg-white dark:bg-astro-secondary text-astro-secondary dark:text-amber-200 shadow-xs'
                  : 'text-astro-muted hover:text-astro-text dark:hover:text-astro-muted'
              }`}
              title="List View"
            >
              <List className="w-3.5 h-3.5" />
            </button>
          </div>

          <button
            onClick={onNewCase}
            className="min-h-[38px] px-3.5 py-1.5 rounded-lg bg-astro-secondary-surface0 hover:bg-astro-secondary text-white text-xs font-bold flex items-center gap-1.5 shadow-xs transition-all cursor-pointer"
          >
            <Plus className="w-4 h-4" />
            <span>{isHi ? '+ नया केस' : '+ New Case'}</span>
          </button>
        </div>
      </div>

      {/* Board View (Horizontal Pipeline Columns) */}
      {viewMode === 'board' ? (
        <div className="overflow-x-auto pb-4 pt-1">
          <div className="flex gap-4 min-w-[1350px]">
            {PIPELINE_STAGES.map((stage) => {
              const stageCases = filteredCases.filter((c) => c.stage === stage.id);
              return (
                <div
                  key={stage.id}
                  className="w-[280px] shrink-0 bg-astro-secondary-surface/70 dark:bg-astro-primary/60 rounded-xl border border-astro-border/80 dark:border-astro-strong-border/80 flex flex-col p-3 gap-3"
                >
                  {/* Column Header */}
                  <div className="flex items-center justify-between gap-2 border-b border-astro-border/80 dark:border-astro-strong-border pb-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-black text-astro-text dark:text-astro-muted">
                          {isHi ? stage.labelHi : stage.labelEn}
                        </span>
                        <span className="text-[11px] font-bold px-1.5 py-0.2 rounded-full bg-astro-card dark:bg-astro-secondary text-astro-secondary-text dark:text-astro-muted">
                          {stageCases.length}
                        </span>
                      </div>
                      <span className="text-[10px] text-astro-muted dark:text-astro-muted">
                        {stage.descEn}
                      </span>
                    </div>
                  </div>

                  {/* Column Cases */}
                  <div className="space-y-3 flex-1 overflow-y-auto max-h-[calc(100vh-320px)] pr-1">
                    {stageCases.length > 0 ? (
                      stageCases.map((caseItem) => (
                        <CaseCard
                          key={caseItem.id}
                          caseItem={caseItem}
                          lang={lang}
                          onOpenCaseWorkspace={onOpenCaseWorkspace}
                          onOpenProAnalysis={onOpenProAnalysis}
                          onOpenAcharyaConsultation={onOpenAcharyaConsultation}
                          onPrepareReport={onPrepareReport}
                          onMoveStage={onMoveStage}
                        />
                      ))
                    ) : (
                      <div className="h-28 border border-dashed border-astro-border dark:border-astro-strong-border/60 rounded-lg flex items-center justify-center text-center p-3">
                        <span className="text-[11px] text-astro-muted dark:text-astro-muted">
                          {isHi ? 'कोई केस नहीं' : 'No cases in this stage'}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        /* List View */
        <div className="bg-white dark:bg-slate-850 rounded-xl border border-astro-border/90 dark:border-astro-strong-border/80 overflow-hidden shadow-xs">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="bg-astro-secondary-surface dark:bg-astro-primary border-b border-astro-border dark:border-astro-strong-border text-astro-secondary-text dark:text-astro-muted font-bold">
                  <th className="py-3 px-4">Case #</th>
                  <th className="py-3 px-4">Client</th>
                  <th className="py-3 px-4">Theme & Question</th>
                  <th className="py-3 px-4">Stage</th>
                  <th className="py-3 px-4">Priority</th>
                  <th className="py-3 px-4">Practitioner</th>
                  <th className="py-3 px-4">Last Activity</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {filteredCases.map((caseItem) => (
                  <tr
                    key={caseItem.id}
                    className="hover:bg-astro-secondary-surface/70 dark:hover:bg-astro-secondary/40 transition-colors"
                  >
                    <td className="py-3 px-4 font-mono font-bold text-[#52635F] dark:text-amber-400">
                      {caseItem.caseNumber}
                    </td>
                    <td className="py-3 px-4 font-bold text-astro-text dark:text-astro-muted">
                      {caseItem.clientName}
                    </td>
                    <td className="py-3 px-4 max-w-xs truncate">
                      <div className="font-semibold text-astro-text dark:text-astro-muted">
                        {isHi ? caseItem.themeTitleHi : caseItem.themeTitleEn}
                      </div>
                      <div className="text-[11px] text-astro-muted dark:text-astro-muted truncate italic">
                        {caseItem.targetQuestion}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <select
                        value={caseItem.stage}
                        onChange={(e) => onMoveStage(caseItem.id, e.target.value as CaseStage)}
                        className="text-[11px] font-bold px-2 py-1 rounded bg-astro-secondary-surface dark:bg-astro-secondary border border-astro-border dark:border-astro-strong-border text-astro-text dark:text-astro-muted"
                      >
                        {PIPELINE_STAGES.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.labelEn}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="py-3 px-4">
                      <span
                        className={`text-[10px] font-bold px-2 py-0.5 rounded ${
                          caseItem.priority === 'HIGH'
                            ? 'bg-rose-500/15 text-rose-700 dark:text-rose-300'
                            : 'bg-astro-secondary-surface dark:bg-astro-secondary text-astro-secondary-text dark:text-astro-muted'
                        }`}
                      >
                        {caseItem.priority}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-astro-secondary-text dark:text-astro-muted">
                      {caseItem.assignedPractitioner}
                    </td>
                    <td className="py-3 px-4 text-astro-muted dark:text-astro-muted">
                      {caseItem.lastActivityDate}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <button
                        onClick={() => onOpenCaseWorkspace(caseItem)}
                        className="px-2.5 py-1 rounded-md bg-astro-secondary-surface0 hover:bg-astro-secondary text-white font-bold text-xs cursor-pointer"
                      >
                        Open
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
